import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  Input,
  Select,
  Option,
  Stack,
  Typography,
  Alert,
  Divider,
} from '@mui/joy';
import { Add, Delete, Edit, Visibility, Publish, CalendarToday, Schedule, ContentCopy, FilterList, Download } from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { scheduleService } from '@/services/scheduleService';
import { shiftTypeService } from '@/services/shiftTypeService';
import { tagService } from '@/services/tagService';
import { userService } from '@/services/userService';
import { ScheduleWithDetails } from '@/types/schedule';
import { ShiftType } from '@/types/shiftType';
import { User } from '@/types/user';
import toast from 'react-hot-toast';
import CopySchedulesModal from '@/components/CopySchedulesModal';
import BulkScheduleModal from '@/components/BulkScheduleModal';
import ScheduleFormModal from '@/components/ScheduleFormModal';
import ScheduleFilters, { ScheduleFilters as ScheduleFiltersType } from '@/components/ScheduleFilters';
import { ScheduleTagManager } from '@/components/ScheduleTagManager';
import ExportModal from '@/components/ExportModal';


export default function SchedulePage() {
  const { permissions } = useAuth();
  const [schedules, setSchedules] = useState<ScheduleWithDetails[]>([]);
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [scheduleTags, setScheduleTags] = useState<Record<number, any[]>>({});
  const [allTags, setAllTags] = useState<Array<{ id: number; name: string; color: string }>>([]);

  const [allEmployees, setAllEmployees] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ScheduleWithDetails | null>(null);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ScheduleFiltersType>({
    hidePastSchedules: true,
    shiftTypeId: null,
    dateRange: null,
    status: 'all',
    tagIds: [],
    userId: null,
    showInactiveUsersOnly: false,
  });

  // Helper function to check permissions
  const hasPermission = (feature: string, action: string) => {
    const permissionName = `${feature}.${action}`;
    const hasPerm = permissions.includes(permissionName);
    return hasPerm;
  };

  // Fetch schedules and tags
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [schedulesData, shiftTypesData, employeesData, tagsData] = await Promise.all([
          scheduleService.getAllSchedules(),
          shiftTypeService.getAllShiftTypes(),
          userService.getEmployees(),
          tagService.getAllTags({ is_active: true }),
        ]);

        setSchedules(schedulesData);
        setShiftTypes(shiftTypesData);
        setAllEmployees(employeesData);
        setAllTags(tagsData.map(tag => ({ id: tag.id, name: tag.name, color: tag.color })));

        // Fetch tags for all schedules in bulk
        if (schedulesData.length > 0) {
          try {
            const scheduleIds = schedulesData.map(schedule => schedule.id);
            const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
            setScheduleTags(tagsData);
          } catch (error) {
            console.error('SchedulePage: Failed to fetch schedule tags:', error);
            // Don't show error toast for tags, as it's not critical
          }
        }
      } catch (error) {
        console.error('SchedulePage: Failed to fetch data:', error);
        toast.error('Failed to load schedules');
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if we have permissions loaded and haven't loaded data yet
    if (permissions.length > 0 && schedules.length === 0) {
      fetchData();
    } 
  }, [permissions]); // Remove user from dependencies to prevent unnecessary re-fetches

  const handleOpenModal = (schedule?: ScheduleWithDetails) => {
    if (schedule) {
      setEditingSchedule(schedule);
    } else {
      setEditingSchedule(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSchedule(null);
  };

  const handleScheduleSuccess = async () => {
    // Refresh schedules after successful create/update
    try {
      setIsRefreshing(true);
      const updatedSchedules = await scheduleService.getAllSchedules();
      setSchedules(updatedSchedules);
      
      // Refresh tags if there are schedules
      if (updatedSchedules.length > 0) {
        const scheduleIds = updatedSchedules.map(s => s.id);
        const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
        setScheduleTags(tagsData);
      }
    } catch (error) {
      console.error('Failed to refresh schedules:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenCopyModal = () => {
    setIsCopyModalOpen(true);
  };

  const handleCloseCopyModal = () => {
    setIsCopyModalOpen(false);
  };

  const handleCopySuccess = () => {
    // Refresh schedules after successful copy
    window.location.reload();
  };

  const handleOpenBulkModal = () => {
    setIsBulkModalOpen(true);
  };

  const handleCloseBulkModal = () => {
    setIsBulkModalOpen(false);
  };

  const handleBulkSuccess = () => {
    // Refresh schedules after successful bulk creation
    const fetchData = async () => {
      if (isRefreshing) return; // Prevent multiple simultaneous requests
      
      try {
        setIsRefreshing(true);
        const updatedSchedules = await scheduleService.getAllSchedules();
        setSchedules(updatedSchedules);
        
        // Refresh tags if there are schedules
        if (updatedSchedules.length > 0) {
          const scheduleIds = updatedSchedules.map(s => s.id);
          const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
          setScheduleTags(tagsData);
        }
      } catch (error) {
        console.error('Failed to refresh schedules after bulk creation:', error);
      } finally {
        setIsRefreshing(false);
      }
    };
    fetchData();
  };


  const handleDelete = async (scheduleId: number) => {
    if (!confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    if (isRefreshing) return; // Prevent multiple simultaneous requests

    try {
      setIsRefreshing(true);
      await scheduleService.deleteSchedule(scheduleId);
      setSchedules(schedules.filter(s => s.id !== scheduleId));
      toast.success('Schedule deleted successfully');
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      toast.error('Failed to delete schedule');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePublish = async (scheduleId: number) => {
    if (isRefreshing) return; // Prevent multiple simultaneous requests

    try {
      setIsRefreshing(true);
      await scheduleService.publishSchedules({ scheduleIds: [scheduleId] });
      setSchedules(schedules.map(s => 
        s.id === scheduleId ? { ...s, status: 'published' } : s
      ));
      toast.success('Schedule published successfully');
    } catch (error) {
      console.error('Failed to publish schedule:', error);
      toast.error('Failed to publish schedule');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter schedules based on current filters
  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => {
      const now = new Date();
      const scheduleStart = new Date(schedule.start_datetime);
      const scheduleEnd = new Date(schedule.end_datetime);

      // Hide past schedules filter
      if (filters.hidePastSchedules && scheduleEnd < now) {
        return false;
      }

      // Shift type filter
      if (filters.shiftTypeId && schedule.shift_type_id !== filters.shiftTypeId) {
        return false;
      }

      // Date range filter
      if (filters.dateRange && filters.dateRange.start && filters.dateRange.end) {
        const startDate = new Date(filters.dateRange.start);
        const endDate = new Date(filters.dateRange.end);
        endDate.setHours(23, 59, 59, 999); // Include the entire end date
        
        // Check if schedule overlaps with the date range
        if (scheduleStart > endDate || scheduleEnd < startDate) {
          return false;
        }
      }

      // Status filter
      if (filters.status !== 'all' && schedule.status !== filters.status) {
        return false;
      }

      // Tags filter
      if (filters.tagIds && filters.tagIds.length > 0) {
        const scheduleTagIds = (scheduleTags[schedule.id] || []).map((st: any) => st.tag_id || st.tag?.id || st.id);
        const hasMatchingTag = filters.tagIds.some(tagId => scheduleTagIds.includes(tagId));
        if (!hasMatchingTag) {
          return false;
        }
      }

      // User filter
      if (filters.userId && schedule.employee_id !== filters.userId) {
        return false;
      }

      // Show inactive users only filter
      if (filters.showInactiveUsersOnly) {
        if (schedule.employee?.is_active !== false) {
          return false;
        }
      }

      return true;
    });
  }, [schedules, filters, scheduleTags]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'updated':
        return 'primary';
      case 'draft':
      default:
        return 'warning';
    }
  };



  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading schedules...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography level="h1" mb={1}>Schedules</Typography>
          <Typography level="body-sm" color="neutral">
            Manage employee schedules and shift assignments
            {isRefreshing && (
              <Typography level="body-xs" color="primary" ml={1}>
                (Refreshing...)
              </Typography>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          {hasPermission('schedules', 'view') && (
            <Button
              startDecorator={<Download />}
              onClick={() => setIsExportModalOpen(true)}
              size="sm"
              variant="outlined"
            >
              Export
            </Button>
          )}
          <Button
            startDecorator={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            size="sm"
            variant="outlined"
            color={showFilters ? "primary" : "neutral"}
          >
            Filters
          </Button>
          {hasPermission('schedules', 'edit') && (
            <Button
              startDecorator={<ContentCopy />}
              onClick={handleOpenCopyModal}
              size="sm"
              variant="outlined"
            >
              Copy Schedules
            </Button>
          )}
          {hasPermission('schedules', 'create') && (
            <>
              <Button
                startDecorator={<Add />}
                onClick={() => handleOpenModal()}
                size="sm"
                variant="outlined"
              >
                Add Schedule
              </Button>
              <Button
                startDecorator={<Add />}
                onClick={handleOpenBulkModal}
                size="sm"
              >
                Bulk Schedule
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* Filters Section */}
      {showFilters && (
        <Box mb={3}>
          <ScheduleFilters
            shiftTypes={shiftTypes}
            filters={filters}
            onFiltersChange={setFilters}
            tags={allTags}
            users={allEmployees}
            onClose={() => setShowFilters(false)}
          />
        </Box>
      )}

      {/* Filter Summary */}
      {showFilters && (filters.hidePastSchedules || filters.shiftTypeId || (filters.dateRange && filters.dateRange.start && filters.dateRange.end) || filters.status !== 'all' || (filters.tagIds && filters.tagIds.length > 0) || filters.userId || filters.showInactiveUsersOnly) && (
        <Box mb={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography level="body-sm" color="neutral">
              Showing {filteredSchedules.length} of {schedules.length} schedules
            </Typography>
            {filters.hidePastSchedules && (
              <Chip size="sm" variant="soft" color="primary">
                Hide Past
              </Chip>
            )}
            {filters.shiftTypeId && (
              <Chip size="sm" variant="soft" color="primary">
                {shiftTypes.find(st => st.id === filters.shiftTypeId)?.name || 'Unknown Shift Type'}
              </Chip>
            )}
            {filters.dateRange && (
              <Chip size="sm" variant="soft" color="primary">
                {new Date(filters.dateRange.start).toLocaleDateString()} - {new Date(filters.dateRange.end).toLocaleDateString()}
              </Chip>
            )}
            {filters.status !== 'all' && (
              <Chip size="sm" variant="soft" color="primary">
                {filters.status}
              </Chip>
            )}
            {filters.tagIds && filters.tagIds.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {filters.tagIds.map(tagId => {
                  const tag = allTags.find(t => t.id === tagId);
                  return tag ? (
                    <Chip key={tagId} size="sm" variant="soft" sx={{ backgroundColor: `${tag.color}20`, color: tag.color }}>
                      {tag.name}
                    </Chip>
                  ) : null;
                })}
              </Stack>
            )}
            {filters.userId && (
              <Chip size="sm" variant="soft" color="primary">
                {allEmployees.find(u => u.id === filters.userId)?.first_name} {allEmployees.find(u => u.id === filters.userId)?.last_name}
              </Chip>
            )}
            {filters.showInactiveUsersOnly && (
              <Chip size="sm" variant="soft" color="danger">
                Inactive Users Only
              </Chip>
            )}
          </Stack>
        </Box>
      )}

      <Grid container spacing={2}>
        {filteredSchedules.map((schedule) => {
          const isInactiveUser = schedule.employee?.is_active === false;
          return (
          <Grid xs={12} md={6} lg={4} key={schedule.id}>
            <Card
              variant={isInactiveUser ? "outlined" : undefined}
              color={isInactiveUser ? "danger" : undefined}
              sx={isInactiveUser ? {
                borderWidth: 2,
                borderColor: 'var(--joy-palette-danger-500)'
              } : undefined}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography level="h4" mb={1}>
                      {schedule.employee?.first_name} {schedule.employee?.last_name}
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                      {schedule.shift_type?.name}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {hasPermission('schedules', 'view') && (
                      <IconButton
                        size="sm"
                        variant="outlined"
                        onClick={() => handleOpenModal(schedule)}
                      >
                        <Visibility />
                      </IconButton>
                    )}
                    {hasPermission('schedules', 'edit') && (
                      <IconButton
                        size="sm"
                        variant="outlined"
                        onClick={() => handleOpenModal(schedule)}
                      >
                        <Edit />
                      </IconButton>
                    )}
                    {hasPermission('schedules', 'delete') && (
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="danger"
                        onClick={() => handleDelete(schedule.id)}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>

                <Stack spacing={1} mb={2}>
                  <Box>
                    <Typography level="body-sm" color="neutral">
                      <CalendarToday sx={{ fontSize: 16, mr: 0.5 }} />
                      {formatDateTime(schedule.start_datetime)} - {formatDateTime(schedule.end_datetime)}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography level="body-sm" color="neutral">
                      <Schedule sx={{ fontSize: 16, mr: 0.5 }} />
                      Created by: {schedule.created_by_user?.first_name} {schedule.created_by_user?.last_name}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Chip
                    size="sm"
                    color={getStatusColor(schedule.status)}
                    variant="soft"
                  >
                    {schedule.status}
                  </Chip>
                  
                  {schedule.status === 'draft' && hasPermission('schedules', 'publish') && (
                    <Button
                      size="sm"
                      startDecorator={<Publish />}
                      onClick={() => handlePublish(schedule.id)}
                    >
                      Publish
                    </Button>
                  )}
                </Stack>

                {/* Tags Section */}
                <Box mt={2}>
                  <ScheduleTagManager
                    scheduleId={schedule.id}
                    preFetchedTags={scheduleTags[schedule.id] || []}
                    onTagsChange={() => {
                      // Refresh schedules and tags to get updated information
                      const refreshData = async () => {
                        if (isRefreshing) return; // Prevent multiple simultaneous requests
                        
                        try {
                          setIsRefreshing(true);
                          const [updatedSchedules] = await Promise.all([
                            scheduleService.getAllSchedules(),
                          ]);
                          setSchedules(updatedSchedules);
                          
                          // Refresh tags in bulk
                          if (updatedSchedules.length > 0) {
                            const scheduleIds = updatedSchedules.map(s => s.id);
                            const tagsData = await tagService.getBulkScheduleTags(scheduleIds);
                            setScheduleTags(tagsData);
                          }
                        } catch (error) {
                          console.error('Failed to refresh data after tag change:', error);
                        } finally {
                          setIsRefreshing(false);
                        }
                      };
                      refreshData();
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
        })}
      </Grid>

      {filteredSchedules.length === 0 && (
        <Card>
          <CardContent>
            <Stack alignItems="center" spacing={2} py={4}>
              <Typography level="h4" color="neutral">
                {schedules.length === 0 ? 'No schedules found' : 'No schedules match your filters'}
              </Typography>
              <Typography level="body-sm" color="neutral" textAlign="center">
                {schedules.length === 0
                  ? (hasPermission('schedules', 'create')
                      ? 'Create your first schedule to get started.'
                      : 'No schedules are available to view.')
                  : 'Try adjusting your filters to see more results.'
                }
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Schedule Modal */}
      <ScheduleFormModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleScheduleSuccess}
        editingSchedule={editingSchedule}
      />

      {/* Copy Schedules Modal */}
      <CopySchedulesModal
        open={isCopyModalOpen}
        onClose={handleCloseCopyModal}
        onSuccess={handleCopySuccess}
      />

      {/* Bulk Schedule Modal */}
      <BulkScheduleModal
        open={isBulkModalOpen}
        onClose={handleCloseBulkModal}
        onSuccess={handleBulkSuccess}
      />

      {/* Export Modal */}
      <ExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        shiftTypes={shiftTypes}
        employees={allEmployees}
        onExportComplete={() => {
          // Optionally refresh schedules after export
        }}
      />
    </Box>
  );
} 